// Payment Setup (Buyer) — a guided, plain-language flow to connect the bank
// account loan payments will come from. Track C: the buyer can connect their
// bank as soon as they have a portal account — captured on Phoenix's platform
// Stripe account (display/reference only, never charged) — and once a loan
// boards onto a real servicer they explicitly RE-AUTHORIZE the bank on the
// servicer's account (Stripe banks are account-scoped and never move
// silently). Post-loan the flow reuses the existing autopay mandate rails.
//
// MULTI-LOAN: a buyer with N boarded loans gets ONE card per loan (each loan's
// own servicer, mandate status, and set-up action). PRE-LOAN: a buyer with M
// in-progress deals (no loan yet) gets ONE pre-loan card per deal, each driving
// its own deal-scoped platform capture (beginPreloan(dealId)). Deal cards and
// loan cards coexist.
//
// Portal rules: no React Query — useEffect+useState read with a cancelled
// guard; writes via useSubmittingMutation.

import { useCallback, useEffect, useState } from "react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { CheckCircle2, Clock } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Skeleton,
  StatusBadge,
  SubmitButton,
  useSubmittingMutation,
  useToast,
  type StatusTone,
} from "@/design-system/components/ui";
import { BankDetailsHelp } from "../components/BankDetailsHelp";
import {
  buyerPaymentSetupApi,
  type BuyerDealPaymentSetup,
  type BuyerLoanPaymentSetup,
  type BuyerLoanPaymentSetupStatus,
  type BuyerPaymentSetupOverview,
  type BuyerPreloanMandateBegin,
} from "../api/paymentSetup";
import {
  portalBuyerFinanceApi,
  type BuyerAutopayMandateBegin,
} from "../api/borrowerFinance";
import { stripeFor } from "../lib/stripeLoader";

const LOAN_STATUS_META: Record<
  BuyerLoanPaymentSetupStatus,
  { label: string; tone: StatusTone }
> = {
  not_started: { label: "Bank not connected", tone: "neutral" },
  in_progress: { label: "In progress", tone: "progress" },
  reauthorize_needed: { label: "Action needed", tone: "warn" },
  servicer_changed: { label: "Action needed", tone: "warn" },
  pending_servicer: { label: "Pending servicer", tone: "progress" },
  complete: { label: "Connected", tone: "ok" },
};

export function BuyerPaymentSetupTab() {
  const [overview, setOverview] = useState<BuyerPaymentSetupOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Edit state is per-card (only one card edits at a time): a boarded loan's
  // "update bank" affordance, and an in-progress deal's pre-loan "update bank".
  const [editingLoanId, setEditingLoanId] = useState<string | null>(null);
  const [editingDealId, setEditingDealId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const data = await buyerPaymentSetupApi.get();
    setOverview(data);
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await buyerPaymentSetupApi.get();
        if (!cancelled) setOverview(data);
      } catch (err) {
        if (!cancelled)
          setError(
            err instanceof Error ? err.message : "Couldn't load your payment setup.",
          );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert" data-testid="payment-setup-error">
        {error}
      </p>
    );
  }
  if (!overview) {
    return <Skeleton className="h-40 w-full" data-testid="payment-setup-loading" />;
  }

  const { loans, deals } = overview;

  // ── Nothing to set up yet ────────────────────────────────────────────────
  if (loans.length === 0 && deals.length === 0) {
    return (
      <div className="space-y-space-4" data-testid="buyer-payment-setup">
        <Card>
          <CardContent>
            <p className="text-small text-gray-mid m-0">
              Nothing to set up yet. Once you have an application in progress or a
              loan, your bank-connect steps will show up here.
            </p>
          </CardContent>
        </Card>
        <BankDetailsHelp context="borrower" />
      </div>
    );
  }

  // ── One pre-loan card per in-progress deal + one setup card per loan ─────
  // Deal cards drive a per-deal platform capture (BeginPreloanAsync(dealId));
  // loan cards drive the boarded-loan servicer mandate.
  return (
    <div className="space-y-space-4" data-testid="buyer-payment-setup">
      {deals.length > 0 && (
        <div className="space-y-space-3">
          <h2 className="text-small font-semibold uppercase tracking-wide text-gray-mid m-0">
            Your applications ({deals.length})
          </h2>
          {deals.map((deal) => (
            <DealSetupCard
              key={deal.dealId}
              deal={deal}
              editing={editingDealId === deal.dealId}
              onEdit={() => setEditingDealId(deal.dealId)}
              onDone={async () => {
                await reload();
                setEditingDealId(null);
              }}
              onCancel={() => setEditingDealId(null)}
            />
          ))}
        </div>
      )}
      {loans.length > 0 && (
        <div className="space-y-space-3">
          {(loans.length > 1 || deals.length > 0) && (
            <h2 className="text-small font-semibold uppercase tracking-wide text-gray-mid m-0">
              Your loans ({loans.length})
            </h2>
          )}
          {loans.map((loan) => (
            <LoanSetupCard
              key={loan.loanId}
              loan={loan}
              editing={editingLoanId === loan.loanId}
              onEdit={() => setEditingLoanId(loan.loanId)}
              onDone={async () => {
                await reload();
                setEditingLoanId(null);
              }}
              onCancel={() => setEditingLoanId(null)}
            />
          ))}
        </div>
      )}
      <BankDetailsHelp context="borrower" />
    </div>
  );
}

// ── Per-deal pre-loan card (in-progress deal, no loan yet) ────────────────────

function DealSetupCard({
  deal,
  editing,
  onEdit,
  onDone,
  onCancel,
}: {
  deal: BuyerDealPaymentSetup;
  editing: boolean;
  onEdit: () => void;
  onDone: () => Promise<void>;
  onCancel: () => void;
}) {
  // Bank saved DIRECTLY on the deal's assigned servicer → final, nothing more to
  // do at boarding (distinct from the platform captured_preloan state below).
  if (deal.status === "complete" && !editing) {
    return (
      <DealBankFinalCard deal={deal} bankLast4={deal.accountLast4} onUpdate={onEdit} />
    );
  }

  // Captured pre-loan bank on the PLATFORM for this deal (no servicer assigned
  // yet) → the "Bank saved, we'll confirm at boarding" display (unless editing).
  if (deal.status === "captured_preloan" && !editing) {
    return (
      <PreloanCapturedCard
        deal={deal}
        bankLast4={deal.accountLast4}
        onUpdate={onEdit}
      />
    );
  }

  // not_started / in_progress / servicer_changed (or editing) → connect action,
  // reusing PreloanConnectSection with THIS deal's id. The copy adapts to whether
  // the deal already has a servicer (connecting is final) and to the
  // servicer-changed reconnect case.
  const isServicerChanged = deal.status === "servicer_changed";
  return (
    <Card data-testid={`buyer-payment-setup-deal-${deal.dealId}`}>
      <CardContent className="space-y-space-3">
        <DealCardHeading deal={deal} />
        <div className="space-y-space-1">
          <p className="text-small font-semibold text-onyx m-0">
            {isServicerChanged
              ? "Reconnect your bank for your new servicer"
              : "Connect your bank account for loan payments"}
          </p>
          <p className="text-small text-gray-mid m-0">
            {isServicerChanged ? (
              <>
                The servicer for your loan has changed, so the bank you connected
                earlier needs to be reconnected with the new servicer — it only
                takes a minute, nothing is charged, and you didn&apos;t do
                anything wrong.
              </>
            ) : deal.connectsToServicer ? (
              <>
                You can save the checking or savings account your loan payments
                will come from now. It&apos;s saved directly with your loan&apos;s
                servicer, so you&apos;re all set — there&apos;s nothing more to
                confirm later. Connecting doesn&apos;t charge anything.
              </>
            ) : (
              <>
                You can save the checking or savings account your loan payments
                will come from now — no need to wait for your loan to finalize.
                Connecting doesn&apos;t charge anything. When your loan is set up
                with its servicer, we&apos;ll ask you to confirm the bank with
                them.
              </>
            )}
          </p>
        </div>
        <PreloanConnectSection
          dealId={deal.dealId}
          onConnected={onDone}
          onCancel={editing ? onCancel : undefined}
        />
      </CardContent>
    </Card>
  );
}

// ── Pre-loan: bank saved FINAL on the deal's servicer (connected capture) ─────

function DealBankFinalCard({
  deal,
  bankLast4,
  onUpdate,
}: {
  deal: BuyerDealPaymentSetup;
  bankLast4: string | null;
  onUpdate: () => void;
}) {
  return (
    <Card data-testid={`buyer-payment-setup-deal-${deal.dealId}`}>
      <CardContent className="space-y-space-3">
        <DealCardHeading deal={deal} />
        <div className="flex items-center gap-space-2">
          <CheckCircle2 size={20} strokeWidth={1.5} className="text-status-success" aria-hidden />
          <StatusBadge tone="ok">Bank connected</StatusBadge>
        </div>
        <p className="text-small text-onyx m-0">
          Your bank account
          {bankLast4 && (
            <>
              {" "}
              <span className="font-semibold">••••{bankLast4}</span>
            </>
          )}{" "}
          is connected with your loan&apos;s servicer. You&apos;re all set — there&apos;s
          nothing more to confirm when your loan finalizes. Nothing is charged
          until your loan payments begin.
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={onUpdate}
          data-testid="buyer-payment-setup-update"
        >
          Update bank account
        </Button>
      </CardContent>
    </Card>
  );
}

function DealCardHeading({ deal }: { deal: BuyerDealPaymentSetup }) {
  const title = deal.businessName ?? deal.dealNumber ?? "Your application";
  return (
    <div className="space-y-space-1">
      <div className="flex flex-wrap items-center justify-between gap-space-2">
        <h3 className="text-h4 font-semibold text-onyx m-0">{title}</h3>
        <StatusBadge tone="neutral">{deal.stageLabel}</StatusBadge>
      </div>
      {deal.businessName && deal.dealNumber && (
        <p className="text-caption text-gray-mid m-0">{deal.dealNumber}</p>
      )}
    </div>
  );
}

// ── Per-loan setup card ──────────────────────────────────────────────────────

function LoanSetupCard({
  loan,
  editing,
  onEdit,
  onDone,
  onCancel,
}: {
  loan: BuyerLoanPaymentSetup;
  editing: boolean;
  onEdit: () => void;
  onDone: () => Promise<void>;
  onCancel: () => void;
}) {
  if (loan.status === "complete" && !editing) {
    return <ConnectedLoanCard loan={loan} onUpdate={onEdit} />;
  }
  if (loan.status === "pending_servicer") {
    return <PendingServicerCard loan={loan} />;
  }

  const isReauthorize = loan.status === "reauthorize_needed";
  const isServicerChanged = loan.status === "servicer_changed";
  const meta = LOAN_STATUS_META[loan.status];

  const heading = isServicerChanged
    ? "Reconnect your bank for your new servicer"
    : isReauthorize
      ? "Re-authorize your bank for loan payments"
      : "Connect your bank account for loan payments";
  const connectLabel = isServicerChanged
    ? "Reconnect bank account"
    : isReauthorize
      ? "Re-authorize bank account"
      : "Connect bank account";

  return (
    <Card data-testid={`buyer-payment-setup-loan-${loan.loanId}`}>
      <CardContent className="space-y-space-3">
        <div className="flex flex-wrap items-center justify-between gap-space-2">
          <h3 className="text-h4 font-semibold text-onyx m-0">
            {loan.tpsName ?? "Your loan"}
          </h3>
          <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
        </div>
        <div className="space-y-space-1">
          <p className="text-small font-semibold text-onyx m-0">{heading}</p>
          <p className="text-small text-gray-mid m-0">
            {isServicerChanged ? (
              <>
                The servicer for your loan has changed, so the bank you connected
                earlier
                {loan.accountLast4 && (
                  <> (ending <span className="font-semibold">{loan.accountLast4}</span>)</>
                )}{" "}
                needs to be reconnected with the new servicer — bank
                authorizations are held directly with your servicer and can&apos;t
                move between them. It only takes a minute, nothing is charged now,
                and you didn&apos;t do anything wrong.
              </>
            ) : isReauthorize ? (
              <>
                For your security, bank authorizations are made directly with
                your loan&apos;s servicer, so the bank you saved earlier
                {loan.accountLast4 && (
                  <> (ending <span className="font-semibold">{loan.accountLast4}</span>)</>
                )}{" "}
                needs a quick re-authorization — it only takes a minute, and
                nothing is charged now.
              </>
            ) : (
              <>
                Save the checking or savings account your monthly loan payments
                will come from. Connecting doesn&apos;t charge anything.
              </>
            )}
          </p>
        </div>
        {loan.mandateOnFile && loan.bankName && (
          <p
            className="text-caption text-gray-mid m-0"
            data-testid={`buyer-payment-setup-current-${loan.loanId}`}
          >
            Currently connected: {loan.bankName}
            {loan.accountLast4 && <> ending {loan.accountLast4}</>}.
          </p>
        )}
        <ConnectBankSection
          loanId={loan.loanId}
          connectLabel={connectLabel}
          onConnected={onDone}
          onCancel={editing ? onCancel : undefined}
        />
      </CardContent>
    </Card>
  );
}

// ── Success / connected state (per loan) ─────────────────────────────────────

function ConnectedLoanCard({
  loan,
  onUpdate,
}: {
  loan: BuyerLoanPaymentSetup;
  onUpdate: () => void;
}) {
  return (
    <Card data-testid="buyer-payment-setup-complete">
      <CardContent className="space-y-space-3">
        <div className="flex flex-wrap items-center justify-between gap-space-2">
          <div className="flex items-center gap-space-2">
            <CheckCircle2 size={20} strokeWidth={1.5} className="text-status-success" aria-hidden />
            <StatusBadge tone="ok">Bank account connected</StatusBadge>
          </div>
          {loan.tpsName && (
            <span className="text-small font-medium text-onyx">{loan.tpsName}</span>
          )}
        </div>
        <p className="text-small text-onyx m-0">
          You&apos;re all set. Your loan payments will come from{" "}
          {loan.bankName ? (
            <span className="font-semibold">{loan.bankName}</span>
          ) : (
            "your connected bank account"
          )}
          {loan.accountLast4 && (
            <>
              {" "}
              <span className="font-semibold">••••{loan.accountLast4}</span>
            </>
          )}
          . There&apos;s nothing more to do right now.
        </p>
        {loan.tpsName && (
          <p className="text-caption text-gray-mid m-0">
            Your bank details are held securely by {loan.tpsName}, the licensed
            servicer assigned to your loan.
          </p>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={onUpdate}
          data-testid="buyer-payment-setup-update"
        >
          Update bank account
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Pre-loan: bank captured, waiting for the loan ────────────────────────────

function PreloanCapturedCard({
  bankLast4,
  onUpdate,
  deal,
}: {
  bankLast4: string | null;
  onUpdate: () => void;
  /** When present this is a per-deal captured state — show the deal heading and
   *  use a deal-scoped test id. */
  deal?: BuyerDealPaymentSetup;
}) {
  return (
    <Card
      data-testid={
        deal
          ? `buyer-payment-setup-deal-${deal.dealId}`
          : "buyer-payment-setup-preloan-captured"
      }
    >
      <CardContent className="space-y-space-3">
        {deal && <DealCardHeading deal={deal} />}
        <div className="flex items-center gap-space-2">
          <CheckCircle2 size={20} strokeWidth={1.5} className="text-status-success" aria-hidden />
          <StatusBadge tone="ok">Bank saved</StatusBadge>
        </div>
        <p className="text-small text-onyx m-0">
          Your bank account
          {bankLast4 && (
            <>
              {" "}
              <span className="font-semibold">••••{bankLast4}</span>
            </>
          )}{" "}
          is saved. Once your loan is set up with its licensed servicer,
          we&apos;ll ask you to confirm this bank with them — a quick, one-time
          authorization. Nothing is charged until your loan payments begin.
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={onUpdate}
          data-testid="buyer-payment-setup-update"
        >
          Update bank account
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Boarded: waiting on the servicer's payment account ──────────────────────

function PendingServicerCard({ loan }: { loan: BuyerLoanPaymentSetup }) {
  return (
    <Card data-testid="buyer-payment-setup-pending-servicer">
      <CardContent className="space-y-space-3">
        <div className="flex flex-wrap items-center justify-between gap-space-2">
          <div className="flex items-center gap-space-2">
            <Clock size={20} strokeWidth={1.5} className="text-gray-mid" aria-hidden />
            <StatusBadge tone="progress">Pending servicer onboarding</StatusBadge>
          </div>
          {loan.tpsName && (
            <span className="text-small font-medium text-onyx">{loan.tpsName}</span>
          )}
        </div>
        <p className="text-small text-onyx m-0">
          Your bank
          {loan.accountLast4 && (
            <>
              {" "}
              <span className="font-semibold">••••{loan.accountLast4}</span>
            </>
          )}{" "}
          is saved. Your loan&apos;s servicer is still completing its own payment
          setup — as soon as it&apos;s ready, we&apos;ll notify you to confirm
          your bank with them. There&apos;s nothing you need to do right now.
        </p>
      </CardContent>
    </Card>
  );
}

// ── Pre-loan Stripe bank-connect (PLATFORM SetupIntent) ──────────────────────
// The intent lives on Phoenix's platform account (no stripeAccount), is
// capture-only, and is re-collected with explicit re-consent at boarding.

function PreloanConnectSection({
  dealId,
  onConnected,
  onCancel,
}: {
  /** The in-progress deal this pre-loan capture belongs to — drives the
   *  deal-scoped begin/sync. */
  dealId: string;
  onConnected: () => Promise<void>;
  onCancel?: () => void;
}) {
  const [intent, setIntent] = useState<BuyerPreloanMandateBegin | null>(null);
  const [beginError, setBeginError] = useState<string | null>(null);

  const begin = useSubmittingMutation(
    async () => {
      setBeginError(null);
      const data = await buyerPaymentSetupApi.beginPreloan(dealId);
      setIntent(data);
    },
    {
      onError: (err) =>
        setBeginError(
          err instanceof Error
            ? err.message
            : "We couldn't start the bank connection. Please try again.",
        ),
    },
  );

  return (
    <div className="space-y-space-3" data-testid="buyer-payment-setup-preloan-connect">
      {beginError && (
        <p className="text-small text-status-error m-0" role="alert">
          {beginError}
        </p>
      )}
      {!intent && (
        <div className="flex flex-wrap items-center gap-space-2">
          <Button
            variant="primary"
            size="sm"
            loading={begin.pending}
            disabled={begin.pending}
            onClick={() => void begin.submit()}
            data-testid="buyer-payment-setup-preloan-start"
          >
            Connect bank account
          </Button>
          {onCancel && (
            <Button variant="text" size="sm" onClick={onCancel} disabled={begin.pending}>
              Cancel
            </Button>
          )}
        </div>
      )}
      {intent && (
        <Elements
          // A connected capture (deal has a real servicer) mounts on that
          // servicer's clearing account; the platform fallback has no account.
          stripe={stripeFor(intent.publishableKey, intent.stripeAccount ?? undefined)}
          options={{ clientSecret: intent.clientSecret }}
        >
          <ConfirmMandateForm
            sync={async () => {
              await buyerPaymentSetupApi.syncPreloan(dealId);
            }}
            onConnected={onConnected}
            onCancel={() => {
              setIntent(null);
              onCancel?.();
            }}
          />
        </Elements>
      )}
    </div>
  );
}

// ── Post-loan Stripe bank-connect (us_bank_account SetupIntent) ─────────────
// Reuses the existing autopay mandate rails (begin + sync) so a buyer who
// already connected a bank for autopay never re-enters anything.

function ConnectBankSection({
  loanId,
  connectLabel,
  onConnected,
  onCancel,
}: {
  loanId: string;
  connectLabel: string;
  onConnected: () => Promise<void>;
  onCancel?: () => void;
}) {
  const [intent, setIntent] = useState<BuyerAutopayMandateBegin | null>(null);
  const [beginError, setBeginError] = useState<string | null>(null);

  const begin = useSubmittingMutation(
    async () => {
      setBeginError(null);
      const data = await portalBuyerFinanceApi.beginAutopayMandate(loanId);
      setIntent(data);
    },
    {
      onError: (err) =>
        setBeginError(
          err instanceof Error
            ? err.message
            : "We couldn't start the bank connection. Please try again.",
        ),
    },
  );

  return (
    <div className="space-y-space-3" data-testid="buyer-payment-setup-connect">
      {beginError && (
        <p className="text-small text-status-error m-0" role="alert">
          {beginError}
        </p>
      )}
      {!intent && (
        <div className="flex flex-wrap items-center gap-space-2">
          <Button
            variant="primary"
            size="sm"
            loading={begin.pending}
            disabled={begin.pending}
            onClick={() => void begin.submit()}
            data-testid="buyer-payment-setup-connect-start"
          >
            {connectLabel}
          </Button>
          {onCancel && (
            <Button variant="text" size="sm" onClick={onCancel} disabled={begin.pending}>
              Cancel
            </Button>
          )}
        </div>
      )}
      {intent && (
        <Elements
          stripe={stripeFor(intent.publishableKey, intent.stripeAccount)}
          options={{ clientSecret: intent.clientSecret }}
        >
          <ConfirmMandateForm
            sync={async () => {
              await portalBuyerFinanceApi.autopayMandateSync(loanId);
            }}
            onConnected={onConnected}
            onCancel={() => {
              setIntent(null);
              onCancel?.();
            }}
          />
        </Elements>
      )}
    </div>
  );
}

function ConfirmMandateForm({
  sync,
  onConnected,
  onCancel,
}: {
  /** Post-confirm server sync (platform preloan vs loan autopay rails). */
  sync: () => Promise<void>;
  onConnected: () => Promise<void>;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);

  const confirm = useSubmittingMutation(
    async () => {
      if (!stripe || !elements) return;
      setError(null);
      // The SetupIntent's payment_method_types is us_bank_account, so the
      // Payment Element only offers the bank-account flow (no card tab).
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {},
        redirect: "if_required",
      });
      if (result.error) {
        setError(
          result.error.message ?? "The bank account could not be saved. Please try again.",
        );
        return;
      }
      await sync();
      await onConnected();
      toast.success("Bank account connected.");
    },
    {
      onError: (err) =>
        setError(
          err instanceof Error
            ? err.message
            : "The bank account could not be saved. Please try again.",
        ),
    },
  );

  return (
    <form
      className="flex flex-col gap-space-3"
      onSubmit={(e) => {
        e.preventDefault();
        void confirm.submit();
      }}
    >
      <PaymentElement />
      <p className="text-caption text-gray-mid m-0">
        We save your bank details securely with our payment provider. Nothing is
        charged now.
      </p>
      {error && (
        <p className="text-small text-status-error m-0" role="alert">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-space-2">
        <SubmitButton
          type="submit"
          variant="primary"
          size="sm"
          pending={confirm.pending}
          pendingLabel="Saving…"
          disabled={!stripe || !elements}
          data-testid="buyer-payment-setup-confirm"
        >
          Save bank account
        </SubmitButton>
        <Button
          type="button"
          variant="text"
          size="sm"
          onClick={onCancel}
          disabled={confirm.pending}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
